import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import styles from './ArtistDetails.module.css';

const api = axios.create({
  baseURL: 'https://back-cultura-sje-production.up.railway.app',
});

function formatarData(data) {
  if (!data) return '-';

  const partes = data.split('-');
  if (partes.length !== 3) return data;

  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
}

function formatarBoolean(valor) {
  if (valor === true) return 'Sim';
  if (valor === false) return 'Não';
  return '-';
}

function temValor(valor) {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === 'string' && valor.trim() === '') return false;
  return true;
}

function normalizarLink(link) {
  if (!temValor(link)) return '';

  if (link.startsWith('http://') || link.startsWith('https://')) {
    return link;
  }

  return `https://${link}`;
}

function DetailLinkItem({ label, value, children }) {
  if (!temValor(value)) return null;

  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>

      <a
        href={normalizarLink(value)}
        target="_blank"
        rel="noreferrer"
        className={styles.detailLinkButton}
      >
        {children}
      </a>
    </div>
  );
}

function DetailItem({ label, value }) {
  if (!temValor(value)) return null;

  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  const childrenArray = Array.isArray(children) ? children : [children];
  const visibleChildren = childrenArray.filter(Boolean);

  if (visibleChildren.length === 0) return null;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.detailsGrid}>{visibleChildren}</div>
    </section>
  );
}

export default function ArtistDetails() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    async function fetchArtist() {
      try {
        setLoading(true);
        setErro('');

        const response = await api.get(`/artists/${id}`);
        setArtist(response.data.data);
      } catch (error) {
        setErro('Não foi possível carregar os dados do artista.');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    fetchArtist();
  }, [id]);

  const nomeExibicao = useMemo(() => {
    if (!artist) return '';
    return (
      artist.nomeArtistico ||
      artist.nomeSocial ||
      artist.nomeCompleto ||
      'Artista'
    );
  }, [artist]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Carregando artista...</h1>
          <p className={styles.stateText}>Aguarde enquanto buscamos as informações.</p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Erro ao carregar</h1>
          <p className={styles.errorText}>{erro}</p>
          <Link to="/" className={styles.backButton}>
            Voltar
          </Link>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Artista não encontrado</h1>
          <Link to="/" className={styles.backButton}>
            Voltar
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.headerCard}>
          <div className={styles.artistHeaderInfo}>
            {temValor(artist.fotoArtista) && (
              <img
                src={artist.fotoArtista}
                alt={`Foto de ${nomeExibicao}`}
                className={styles.artistPhoto}
              />
            )}

            <div>
              <p className={styles.badge}>Cadastro de artista</p>
              <h1 className={styles.artistName}>{nomeExibicao}</h1>
              <p className={styles.subtitle}>
                ID #{artist.id} • {artist.linguagemArtistica.join(' • ')}
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
            <Link to="/artistas" className={styles.secondaryButton}>
              Voltar para lista
            </Link>
          </div>
        </div>

        <Section title="Informações pessoais">
          <DetailItem label="Nome completo" value={artist.nomeCompleto} />
          <DetailItem label="Nome social" value={artist.nomeSocial} />
          <DetailItem label="Nome artístico" value={artist.nomeArtistico} />
          <DetailItem label="CPF" value={artist.cpf} />
          <DetailItem label="Data de nascimento" value={formatarData(artist.dataDeNascimento)} />
          <DetailItem label="Nome do pai" value={artist.nomeDoPai} />
          <DetailItem label="Nome da mãe" value={artist.nomeDaMae} />
          <DetailItem label="Gênero" value={artist.genero} />
          <DetailItem label="Escolaridade" value={artist.escolaridade} />
        </Section>

        <Section title="Endereço">
          <DetailItem label="Endereço" value={artist.endereco} />
          <DetailItem label="Número" value={artist.numero} />
          <DetailItem label="Bairro" value={artist.bairro} />
          <DetailItem label="CEP" value={artist.cep} />
          <DetailItem label="Cidade" value={artist.cidade} />
          <DetailItem label="Estado" value={artist.estado} />
          <DetailItem
            label="Morador do município"
            value={formatarBoolean(artist.moradorDoMunicipio)}
          />
        </Section>

        <Section title="Contato e acesso">
          <DetailItem label="Celular" value={artist.celular} />
          <DetailItem label="E-mail" value={artist.email} />
        </Section>

        <Section title="Atuação artística">
          <DetailItem label="Linguagem artística" value={artist.linguagemArtistica.join(' • ')} />
          <DetailItem
            label="Renda individual exclusiva do trabalho artístico"
            value={formatarBoolean(artist.rendaIndividualExclusivoDoTrabalhoArtistico)}
          />
          <DetailItem label="Outras rendas" value={artist.outrasRendas} />
          <DetailItem label="Participa de organização" value={artist.participaDeOrganizacao} />
          <DetailItem label="Vínculo com FUNDARPE" value={formatarBoolean(artist.vinculoFUNDARPE)} />
          <DetailItem
            label="Tocou/toca na FUNDARPE"
            value={formatarBoolean(artist.tocouTocaNaFUNDARPE)}
          />
          <DetailItem label="Espaço de apresentação" value={artist.espacoApresentacao} />
          <DetailItem
            label="Tempo de atividade artística"
            value={artist.tempoDeAtividadeArtistica}
          />
          <DetailItem
            label="Apresentações por semana"
            value={artist.apresentacaoPorSemanada}
          />
        </Section>

        <Section title="Links e materiais">
          <DetailItem label="Links de matérias" value={artist.linksDeMaterias} />
          <DetailItem label="Página do artista" value={artist.linkPaginaDoArtista} />
          <DetailLinkItem label="Currículo" value={artist.linkCurriculo}>
            Ver currículo
          </DetailLinkItem>
        </Section>

        <Section title="Dados bancários">
          <DetailItem label="Banco" value={artist.banco} />
          <DetailItem label="Agência" value={artist.agencia} />
          <DetailItem label="Conta" value={artist.conta} />
          <DetailItem label="Tipo de conta" value={artist.tipoDeConta} />
        </Section>
      </div>
    </div>
  );
}